import { ValidationError, array, lazy, mixed, number, object, string } from 'yup'

import {
  ImportActivityStage,
  ImportActivityStageState,
  ImportApprovalMethod,
  ImportRequestScanResult,
  ImportRequestSource,
  ImportRequestStatus,
  ImportRequestType
} from './enums'
import type { ImportRequestVerificationDetail } from './types'

const importRequestLabelSchema = object({
  _id: string().required(),
  name: string().required()
}).required()

const importRequestUserReferenceSchema = object({
  _id: string().required(),
  email: string().required(),
  name: string().optional()
}).unknown(true)

const createOptionalImportRequestUserReferenceSchema = () =>
  lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return importRequestUserReferenceSchema
  })

const paginationMetaSchema = object({
  totalCount: number().required(),
  totalPages: number().required(),
  currentPage: number().required(),
  perPage: number().required(),
  nextPage: number().nullable().optional(),
  prevPage: number().nullable().optional()
}).required()

const importRequestObjectCountsSchema = object({
  total: number().required(),
  clean: number().required(),
  threatDetected: number().required(),
  unsupported: number().required()
}).required()

const importActivityTimelineEntrySchema = object({
  stage: mixed<ImportActivityStage>().oneOf(Object.values(ImportActivityStage)).required(),
  state: mixed<ImportActivityStageState>().oneOf(Object.values(ImportActivityStageState)).required(),
  startedAt: string().optional(),
  completedAt: string().optional()
}).required()

const sharedVerificationListItemSchema = object({
  _id: string().required(),
  requestType: mixed<ImportRequestType>().oneOf(Object.values(ImportRequestType)).required(),
  status: mixed<ImportRequestStatus>().oneOf(Object.values(ImportRequestStatus)).required(),
  workspaceId: string().required(),
  workspaceName: string().optional(),
  requestingUser: createOptionalImportRequestUserReferenceSchema(),
  assignedTo: createOptionalImportRequestUserReferenceSchema(),
  verifiedBy: createOptionalImportRequestUserReferenceSchema(),
  verifiedAt: string().optional(),
  approvalMethod: mixed<ImportApprovalMethod>().oneOf(Object.values(ImportApprovalMethod)).optional(),
  labels: array(importRequestLabelSchema).required(),
  createdAt: string().required()
})

const uploadedImportVerificationListItemSchema = sharedVerificationListItemSchema
  .shape({
    source: mixed<ImportRequestSource.UserUploaded>().oneOf([ImportRequestSource.UserUploaded]).optional(),
    scanResult: mixed<ImportRequestScanResult>().oneOf(Object.values(ImportRequestScanResult)).required(),
    fileName: string().required(),
    sizeInBytes: number().optional()
  })
  .unknown(true)
  .required()

const externalImportVerificationListItemSchema = sharedVerificationListItemSchema
  .shape({
    source: mixed<ImportRequestSource.ExternalS3Location>().oneOf([ImportRequestSource.ExternalS3Location]).required(),
    sourceUri: string().required(),
    destinationPrefix: string().optional(),
    totalSizeInBytes: number().optional(),
    counts: importRequestObjectCountsSchema.optional()
  })
  .unknown(true)
  .required()

const resolveImportRequestVerificationListItemSchema = (value: unknown) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'source' in value &&
    value.source === ImportRequestSource.ExternalS3Location
  ) {
    return externalImportVerificationListItemSchema
  }

  return uploadedImportVerificationListItemSchema
}

const importRequestVerificationListItemSchema = lazy((value: unknown) =>
  resolveImportRequestVerificationListItemSchema(value)
)

const uploadedImportVerificationDetailSchema = uploadedImportVerificationListItemSchema
  .shape({
    submittedAt: string().required(),
    scanStatusCode: string().optional(),
    scanStartedAt: string().optional(),
    scanCompletedAt: string().optional(),
    eTag: string().optional(),
    quarantine: object().optional().unknown(true),
    destination: object().optional().unknown(true),
    formSubmission: object().optional().unknown(true),
    rejectionReason: string().optional(),
    failureReason: string().optional()
  })
  .unknown(true)
  .required()

const externalImportVerificationDetailSchema = externalImportVerificationListItemSchema
  .shape({
    scannedCount: number().optional(),
    copyStartedAt: string().optional(),
    copyCompletedAt: string().optional(),
    scanResult: mixed<ImportRequestScanResult>().oneOf(Object.values(ImportRequestScanResult)).optional(),
    scanStatusCode: string().optional(),
    scanStartedAt: string().optional(),
    scanCompletedAt: string().optional(),
    failureReason: string().optional(),
    rejectionReason: string().optional(),
    timeline: array(importActivityTimelineEntrySchema).required()
  })
  .unknown(true)
  .required()

export const importRequestVerificationDetailSchema = lazy((value: unknown) => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'source' in value &&
    value.source === ImportRequestSource.ExternalS3Location
  ) {
    return externalImportVerificationDetailSchema
  }

  return uploadedImportVerificationDetailSchema
})

export const parseImportRequestVerificationDetail = (data: unknown): ImportRequestVerificationDetail => {
  try {
    return importRequestVerificationDetailSchema.validateSync(data, {
      strict: false
    }) as ImportRequestVerificationDetail
  } catch (error) {
    const message = error instanceof ValidationError ? error.message : String(error)
    console.warn('[API response validation]', message)
    return data as ImportRequestVerificationDetail
  }
}

export const getImportRequestsVerificationResponseSchema = object({
  data: array(importRequestVerificationListItemSchema).required(),
  pagination: paginationMetaSchema.required()
})
  .unknown(true)
  .required()

export const getImportRequestLabelsResponseSchema = object({
  data: array(importRequestLabelSchema).required()
})
  .unknown(true)
  .required()

const importRequestFilterUserSchema = object({
  _id: string().required(),
  email: string().required()
}).required()

export const getImportRequestFilterUsersResponseSchema = object({
  data: array(importRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

export const getImportRequestFilterAssigneesResponseSchema = object({
  data: array(importRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

export const getAssignableAssigneesForImportRequestResponseSchema = object({
  data: array(importRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

const importRequestFilterWorkspaceSchema = object({
  _id: string().required(),
  name: string().required()
}).required()

export const getImportRequestFilterWorkspacesResponseSchema = object({
  data: array(importRequestFilterWorkspaceSchema).required()
})
  .unknown(true)
  .required()
